import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatCurrency } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  ArrowLeft, AlertTriangle, RefreshCw, ShieldCheck, FileText, Banknote,
  UserCheck, FileSignature, ExternalLink, CheckCircle2, Gavel, XCircle,
  HelpCircle, Loader2,
} from "lucide-react";
import KycDocViewer from "./KycDocViewer";
import ActivityTimeline from "./ActivityTimeline";
import AccountsPanel from "./AccountsPanel";

/**
 * Consolidated participant review pane (participant-console-004) — the one pane
 * where Milen (the external FinBPM partner) decides without tab-switching, the
 * pack's "evidence in one place." READ-ONLY here; the decision actions land in
 * participant-console-006.
 *
 * Consumes 004's detail endpoint:
 *   GET /api/partner/console/participants/{user_id}
 * whose body is the partner-safe projection (the ONLY field set the partner
 * ever sees) PLUS review essentials: drift_flags, kyc_documents (metadata only),
 * status_timeline, and signing_evidence. The pane never assumes a field outside
 * that contract exists.
 *
 * Mounted at the nested `/console/:userId` route (owned by 002's shell). Three
 * slots are intentionally left for later tickets — see the commented
 * placeholders below.
 */

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

// Friendly labels for the drift-able identity fields.
const FIELD_LABELS = {
  full_name: "Full name",
  email: "Email",
  phone: "Phone",
  address: "Address",
  vc_number: "VC #",
  kyc_status: "KYC status",
  accredited_status: "Accredited status",
};

// A labeled value row. When `drift` is true the row is highlighted — the field
// changed since the participant signed (the finbpm snapshot-vs-current
// comparison, surfaced by the backend's drift_flags).
const Row = ({ label, value, drift = false, mono = false, testid }) => (
  <div
    data-testid={testid}
    data-drift={drift ? "true" : undefined}
    className={`flex items-start justify-between gap-4 py-2 px-3 rounded ${
      drift ? "bg-amber-50 border border-amber-300" : ""
    }`}
  >
    <span className="text-xs uppercase tracking-wide text-slate-500 shrink-0 pt-0.5">
      {label}
    </span>
    <span className="flex items-center gap-2 text-sm text-slate-900 text-right min-w-0">
      {drift && <AlertTriangle size={13} className="text-amber-600 shrink-0" />}
      <span className={`truncate ${mono ? "font-mono text-xs" : ""}`}>
        {value || value === 0 ? value : "—"}
      </span>
    </span>
  </div>
);

const Section = ({ icon: Icon, title, children, testid }) => (
  <Card className="bg-white border-slate-200" data-testid={testid}>
    <CardHeader className="py-3">
      <CardTitle className="text-slate-900 text-base flex items-center gap-2">
        {Icon && <Icon size={16} className="text-burgundy" />}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0 space-y-1">{children}</CardContent>
  </Card>
);

/**
 * Decision panel (participant-console-006) — the act the console exists for.
 *
 * Three actions, all routed to the SAME backend decision endpoint
 * (`POST /partner/console/participants/{userId}/decision`), which reuses the
 * finbpm-flow-004 legal path (no fork):
 *   - Countersign  — typed-signature capture + an explicit confirm dialog before
 *     the irreversible legal act; records the partner signature + executed copies.
 *   - Reject       — client-facing reason REQUIRED (the member reads it verbatim).
 *   - Request info — client-facing reason REQUIRED; rides the same member-notify.
 *
 * Optimistically reflects the new status on success, then asks the parent to
 * reload so the timeline + signing-evidence + activity feed re-fetch from source.
 * Hidden once the agreement is already countersigned or rejected — the decision
 * is per-document and not repeatable here.
 */
const DecisionPanel = ({ userId, agreement, onDecided }) => {
  const alreadyCountersigned = !!agreement.partner_signed_at;
  const alreadyRejected = !!agreement.partner_rejected_at;
  const terminal = alreadyCountersigned || alreadyRejected;

  // null | "countersign" | "reject" | "request-info" — which action is open.
  const [mode, setMode] = useState(null);
  const [typedName, setTypedName] = useState("");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);

  const reset = () => {
    setMode(null);
    setTypedName("");
    setReason("");
    setConfirming(false);
    setActionError(null);
  };

  const submit = useCallback(
    async (action, body) => {
      setSubmitting(true);
      setActionError(null);
      try {
        const r = await api.post(
          `/partner/console/participants/${userId}/decision`,
          { action, ...body }
        );
        reset();
        // Optimistic status + a source reload (timeline/evidence/activity).
        onDecided(r.data?.action || action);
      } catch (e) {
        setActionError(
          e.response?.data?.detail || "The decision could not be recorded."
        );
      } finally {
        setSubmitting(false);
      }
    },
    [userId, onDecided]
  );

  if (terminal) {
    return (
      <div
        className="mt-4 rounded-lg border border-slate-200 bg-white p-5"
        data-testid="review-decision-panel"
      >
        <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
          {alreadyCountersigned ? (
            <>
              <CheckCircle2 size={16} className="text-emerald-600" />
              This agreement is countersigned. The decision is final.
            </>
          ) : (
            <>
              <XCircle size={16} className="text-rose-600" />
              This agreement was returned to the participant. No further action
              here until they re-execute.
            </>
          )}
        </p>
      </div>
    );
  }

  return (
    <div
      className="mt-4 rounded-lg border border-slate-200 bg-white p-5"
      data-testid="review-decision-panel"
    >
      <p className="text-sm font-semibold text-slate-900 mb-1">Decision</p>
      <p className="text-xs text-slate-500 mb-4">
        Countersigning is an irreversible legal act and routes executed copies to
        the participant and admin. Reject and Request info send the participant
        your reason verbatim.
      </p>

      {actionError && (
        <div
          className="mb-3 flex items-center gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800"
          data-testid="decision-error"
        >
          <AlertTriangle size={14} className="shrink-0" />
          {actionError}
        </div>
      )}

      {/* Action chooser */}
      {!mode && (
        <div className="flex flex-wrap gap-2" data-testid="decision-actions">
          <Button
            size="sm"
            onClick={() => { reset(); setMode("countersign"); }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="decision-open-countersign"
          >
            <Gavel size={14} className="mr-1.5" />
            Approve &amp; countersign
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { reset(); setMode("request-info"); }}
            className="border-slate-300 text-slate-700"
            data-testid="decision-open-request-info"
          >
            <HelpCircle size={14} className="mr-1.5" />
            Request more info
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { reset(); setMode("reject"); }}
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="decision-open-reject"
          >
            <XCircle size={14} className="mr-1.5" />
            Reject
          </Button>
        </div>
      )}

      {/* Countersign — typed signature + confirm */}
      {mode === "countersign" && (
        <div className="space-y-3" data-testid="decision-countersign-form">
          <div>
            <label
              className="block text-xs uppercase tracking-wide text-slate-500 mb-1"
              htmlFor="decision-typed-name"
            >
              Type the signatory name to countersign
            </label>
            <input
              id="decision-typed-name"
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Full legal name of the signatory"
              autoComplete="off"
              disabled={submitting}
              data-testid="decision-typed-name"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-burgundy focus:outline-none"
            />
            <p className="mt-1 text-xs text-slate-400">
              Recorded as the legal signature with your IP, browser, and a
              timestamp — the same receipt as the tokenized review.
            </p>
          </div>

          {!confirming ? (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => setConfirming(true)}
                disabled={!typedName.trim() || submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="decision-countersign-continue"
              >
                Continue
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={reset}
                disabled={submitting}
                className="border-slate-300 text-slate-600"
                data-testid="decision-cancel"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3"
              data-testid="decision-countersign-confirm"
            >
              <p className="text-sm text-amber-900 mb-3">
                Countersign as <strong>{typedName.trim()}</strong>? This executes
                the agreement and cannot be undone. Executed copies go to the
                participant and admin immediately.
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() =>
                    submit("countersign", { typed_name: typedName.trim() })
                  }
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="decision-countersign-confirm-btn"
                >
                  {submitting && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                  Yes, countersign
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirming(false)}
                  disabled={submitting}
                  className="border-slate-300 text-slate-600"
                  data-testid="decision-countersign-back"
                >
                  Back
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reject / request-info — required client-facing reason */}
      {(mode === "reject" || mode === "request-info") && (
        <div className="space-y-3" data-testid={`decision-${mode}-form`}>
          <div>
            <label
              className="block text-xs uppercase tracking-wide text-slate-500 mb-1"
              htmlFor="decision-reason"
            >
              {mode === "reject"
                ? "Reason for rejection (sent to the participant verbatim)"
                : "What does the participant need to provide? (sent verbatim)"}
            </label>
            <textarea
              id="decision-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              disabled={submitting}
              data-testid="decision-reason"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-burgundy focus:outline-none"
              placeholder="The participant reads this exactly as written."
            />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() =>
                submit(mode, { client_facing_reason: reason.trim() })
              }
              disabled={!reason.trim() || submitting}
              className={
                mode === "reject"
                  ? "bg-rose-600 hover:bg-rose-700 text-white"
                  : "bg-burgundy hover:opacity-90 text-white"
              }
              data-testid={`decision-${mode}-submit`}
            >
              {submitting && <Loader2 size={14} className="mr-1.5 animate-spin" />}
              {mode === "reject" ? "Send rejection" : "Send request"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={reset}
              disabled={submitting}
              className="border-slate-300 text-slate-600"
              data-testid="decision-cancel"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const ParticipantReview = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/partner/console/participants/${userId}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load the participant review.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Decision recorded — optimistically reflect the new agreement status, then
  // reload from source so the timeline, signing-evidence, and activity feed all
  // re-fetch (the backend reused the finbpm legal path; the canonical record is
  // server-side).
  const handleDecided = useCallback(
    (action) => {
      const nowIso = new Date().toISOString();
      setData((prev) => {
        if (!prev) return prev;
        if (action === "countersigned") {
          return {
            ...prev,
            agreement_status: "FinBPM Approved",
            partner_signed_at: nowIso,
          };
        }
        // reject / request-info both return the agreement to the member.
        return {
          ...prev,
          agreement_status: "FinBPM Rejected / More Info Needed",
          partner_rejected_at: nowIso,
        };
      });
      load();
    },
    [load]
  );

  const back = (
    <Button
      size="sm"
      variant="outline"
      onClick={() => navigate("/console")}
      className="border-slate-300 text-slate-700 mb-4"
      data-testid="review-back"
    >
      <ArrowLeft size={14} className="mr-1.5" />
      Back to queue
    </Button>
  );

  if (loading) {
    return (
      <div data-testid="review-loading">
        {back}
        <div className="p-8 text-center text-sm text-slate-500">
          Loading the participant review...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="review-error">
        {back}
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
          <AlertTriangle className="mx-auto text-amber-500 mb-2" size={22} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            className="mt-3 border-slate-300 text-slate-700"
          >
            <RefreshCw size={14} className="mr-1.5" />
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const d = data || {};
  const drift = d.drift_flags || {};
  const driftCount = Object.keys(drift).filter((k) => drift[k]).length;
  const kycDocs = d.kyc_documents || [];
  const timeline = d.status_timeline || [];
  const evidence = d.signing_evidence || {};

  return (
    <div data-testid="participant-review">
      {back}

      {/* HEADER */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <h2 className="text-lg font-serif font-semibold text-slate-900 truncate">
            {d.full_name || "Participant"}
          </h2>
          <p className="text-sm text-slate-500 truncate">{d.email}</p>
        </div>
        <span className="px-2.5 py-1 rounded border text-xs bg-slate-100 text-slate-700 border-slate-300 shrink-0">
          {d.agreement_status || "—"}
        </span>
      </div>

      {driftCount > 0 && (
        <div
          className="mb-5 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="review-drift-banner"
        >
          <AlertTriangle size={15} className="shrink-0" />
          {driftCount} field{driftCount === 1 ? "" : "s"} changed since the
          participant signed — highlighted below.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* IDENTITY & DRIFT */}
        <Section icon={UserCheck} title="Identity &amp; drift" testid="review-section-identity">
          <Row label={FIELD_LABELS.full_name} value={d.full_name} drift={!!drift.full_name} testid="review-row-full_name" />
          <Row label={FIELD_LABELS.email} value={d.email} drift={!!drift.email} testid="review-row-email" />
          <Row label={FIELD_LABELS.phone} value={d.phone} drift={!!drift.phone} testid="review-row-phone" />
          <Row label={FIELD_LABELS.address} value={d.address} drift={!!drift.address} testid="review-row-address" />
          <Row label={FIELD_LABELS.vc_number} value={d.vc_number} drift={!!drift.vc_number} mono testid="review-row-vc_number" />
        </Section>

        {/* KYC & ACCREDITATION */}
        <Section icon={ShieldCheck} title="KYC &amp; accreditation" testid="review-section-kyc">
          <Row label={FIELD_LABELS.kyc_status} value={d.kyc_status} drift={!!drift.kyc_status} testid="review-row-kyc_status" />
          <Row label={FIELD_LABELS.accredited_status} value={d.accredited_status} drift={!!drift.accredited_status} testid="review-row-accredited_status" />

          <div className="pt-2">
            <p className="text-xs uppercase tracking-wide text-slate-500 px-3 mb-1">
              Documents ({kycDocs.length})
            </p>
            {kycDocs.length === 0 ? (
              <p className="px-3 text-sm text-slate-400">No documents on file.</p>
            ) : (
              <ul className="space-y-1" data-testid="review-kyc-list">
                {kycDocs.map((doc) => (
                  <li
                    key={doc.id}
                    data-testid={`review-kyc-doc-${doc.id}`}
                    className="flex items-center justify-between gap-3 px-3 py-1.5 rounded hover:bg-slate-50"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <FileText size={14} className="text-slate-400 shrink-0" />
                      <span className="text-sm text-slate-900 truncate">
                        {doc.file_name || doc.document_type || "Document"}
                      </span>
                    </span>
                    <span className="text-xs text-slate-500 shrink-0">{doc.status || "—"}</span>
                  </li>
                ))}
              </ul>
            )}

            {/*
              participant-console-005 inline KYC document viewer. The list above
              is METADATA ONLY — the detail endpoint never serves document bytes.
              The viewer fetches bytes from its own partner doc-bytes endpoint
              (partner_console_docs.py); this pane does not wire byte-fetching.
            */}
            <div className="pt-2">
              <KycDocViewer userId={userId} />
            </div>
          </div>
        </Section>

        {/* AGREEMENT & AMOUNT */}
        <Section icon={CheckCircle2} title="Agreement &amp; amount" testid="review-section-agreement">
          <Row label="Status" value={d.agreement_status} testid="review-row-agreement_status" />
          <Row label="Amount elected" value={d.amount_elected != null ? formatCurrency(d.amount_elected) : null} testid="review-row-amount" />
          <Row label="Terms version" value={d.terms_version} mono testid="review-row-terms_version" />
          {d.pilot_label && <Row label="Pilot" value={d.pilot_label} testid="review-row-pilot" />}

          <div className="pt-2">
            <p className="text-xs uppercase tracking-wide text-slate-500 px-3 mb-1">Timeline</p>
            {timeline.length === 0 ? (
              <p className="px-3 text-sm text-slate-400">No status history.</p>
            ) : (
              <ol className="space-y-1" data-testid="review-timeline">
                {timeline.map((e, i) => (
                  <li
                    key={`${e.status}-${i}`}
                    className="flex items-center justify-between gap-3 px-3 py-1 text-sm"
                  >
                    <span className="text-slate-900">{e.status}</span>
                    <span className="text-xs text-slate-500">{fmtDateTime(e.at)}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </Section>

        {/* FUNDING ELECTION */}
        <Section icon={Banknote} title="Funding election" testid="review-section-funding">
          <Row label="Method" value={d.funding_method} testid="review-row-funding_method" />
          <Row label="Asset" value={d.funding_asset} testid="review-row-funding_asset" />
          <Row label="Amount" value={d.amount_elected != null ? formatCurrency(d.amount_elected) : null} testid="review-row-funding_amount" />
        </Section>

        {/* SIGNING EVIDENCE */}
        <Section icon={FileSignature} title="Signing evidence" testid="review-section-evidence">
          <Row label="Verification ID" value={evidence.verification_id} mono testid="review-row-verification_id" />
          <Row label="PDF SHA-256" value={evidence.signed_pdf_sha256} mono testid="review-row-sha256" />
          <Row label="Investor signed" value={fmtDateTime(d.investor_signed_at)} testid="review-row-investor_signed_at" />
          <Row label="Partner signed" value={fmtDateTime(d.partner_signed_at)} testid="review-row-partner_signed_at" />
          {d.partner_rejected_at && (
            <Row label="Partner rejected" value={fmtDateTime(d.partner_rejected_at)} testid="review-row-partner_rejected_at" />
          )}
          <div className="flex flex-wrap items-center gap-2 px-3 pt-2">
            {evidence.verify_url && (
              <a
                href={evidence.verify_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="review-verify-link"
                className="inline-flex items-center gap-1.5 text-sm text-burgundy hover:underline"
              >
                <ExternalLink size={13} />
                Public verification
              </a>
            )}
            {evidence.signed_pdf_available ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                <FileText size={13} />
                Signed PDF on file
              </span>
            ) : (
              <span className="text-xs text-slate-400">No signed PDF on file</span>
            )}
          </div>
        </Section>
      </div>

      {/*
        participant-console-006 decision panel — countersign / reject /
        request-info. Calls the decision handler that extends
        partner_console_detail.py, which reuses the finbpm-flow-004 legal path
        (no fork). Countersign is a per-document legal act (no bulk — DECISION 4).
      */}
      <DecisionPanel userId={userId} agreement={d} onDecided={handleDecided} />

      {/*
        STARSEED-CAPMARKETS-173 — always rendered, deliberately. Unlike the
        decision panel above it does NOT hide once the agreement is terminal:
        the accounts are commonly created after countersigning, and 172 forbids
        this pack from assuming an order at all.

        `onAttached` is `load`, NOT `handleDecided`. `handleDecided` maps an
        action to an optimistic agreement status and treats anything that is
        not "countersigned" as a rejection — so passing it here would have
        flipped the member to "FinBPM Rejected / More Info Needed" every time
        an account was attached. An attach is not a decision; it only needs the
        detail reloaded.
      */}
      <AccountsPanel userId={userId} onAttached={load} />

      {/*
        participant-console-007 partner-action activity timeline — countersign /
        reject / request-info / view events from the dedicated
        partner_console_audit collection. Distinct from the AGREEMENT lifecycle
        status_timeline above. Mounted after the decision panel so a fresh action
        appears here on the post-decision reload.
      */}
      <div className="mt-4">
        <ActivityTimeline userId={userId} />
      </div>
    </div>
  );
};

export default ParticipantReview;
